'use client';

import { useCallback, useMemo } from 'react';

import { type Asset } from '@air/api/types';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';

import { GenerateCdnLinkModal } from '~/components/asset-detail/GenerateCdnLinkModal';
import { type VersionReference } from '~/components/asset-detail/ImageOptionsSection';
import { type ButtonProps } from '~/components/Button';
import { useAssetPermissions } from '~/hooks/api/useAssetPermissions';
import { useListCdnLinks } from '~/hooks/api/useListCdnLinks';

export type UseCdnLinkParams = {
  /** The asset the link belongs to. The hook only ever needed the id, never the whole clip. */
  assetId: Asset['id'] | undefined;
  workspaceId: string;
  setVersionReference: (value: VersionReference) => void;
  /** Button size for the Generate CDN modal CTAs. Forwarded to `GenerateCdnLinkModal`. */
  cdnLinkModalButtonSize?: ButtonProps['size'];
};

export const useCdnLink = ({ assetId, workspaceId, setVersionReference, cdnLinkModalButtonSize }: UseCdnLinkParams) => {
  const { showToast } = useToasts();

  const { data: cdnLinksData, isPending: isCdnLinksPending } = useListCdnLinks({
    assetId,
    followsDefaultVersion: true,
    workspaceId,
  });

  const { data: permissionsByAssetId, isPending: isPermissionsPending } = useAssetPermissions({
    assetIds: [assetId],
    workspaceId,
  });

  const cdnLinks = cdnLinksData?.data;
  const activeCdnLink = useMemo(() => cdnLinks?.find((link) => !link.inactive), [cdnLinks]);
  const hasActiveCdnLink = !!activeCdnLink;
  const inactiveCdnLink = useMemo(() => cdnLinks?.find((link) => link.inactive), [cdnLinks]);
  const canEditAsset = (assetId ? permissionsByAssetId?.[assetId]?.asset?.canEdit : false) ?? false;
  const isDynamicEnabled = hasActiveCdnLink || canEditAsset;

  // Both queries feed the decision below, so a caller acting on its own (rather than on a user
  // click) has to wait for both: acting early reads "no active link" off an unresolved query and
  // prompts to create one that already exists.
  const isLoading = isCdnLinksPending || isPermissionsPending;

  const [showCdnLinkModal, hideCdnLinkModal] = useAirModal(GenerateCdnLinkModal);

  const handleVersionReferenceChange = useCallback(
    (value: VersionReference) => {
      if (value === 'static') {
        setVersionReference('static');
        return;
      }

      if (hasActiveCdnLink) {
        setVersionReference('dynamic');
        return;
      }

      if (!assetId) {
        return;
      }

      showCdnLinkModal({
        assetId,
        buttonSize: cdnLinkModalButtonSize,
        inactiveCdnLinkId: inactiveCdnLink?.id,
        onError: () => {
          showToast('Failed to create CDN link. Please try again.', { color: 'red' });
        },
        onSuccess: () => {
          setVersionReference('dynamic');
          hideCdnLinkModal();
        },
        workspaceId,
      });
    },
    [
      assetId,
      cdnLinkModalButtonSize,
      hasActiveCdnLink,
      hideCdnLinkModal,
      inactiveCdnLink?.id,
      setVersionReference,
      showCdnLinkModal,
      showToast,
      workspaceId,
    ],
  );

  return {
    activeCdnLink,
    handleVersionReferenceChange,
    isDynamicEnabled,
    isLoading,
  };
};
